import Button from 'components/base/Button'
import FormItem from 'components/base/FormItem'
import { FC } from 'lib/component-utils'
import { useUserContext } from 'lib/context'
import { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Updater } from 'use-immer'
import { RecoveryFormData, RecoveryProcessState } from './lib/types'
import { ArrowNarrowLeftIcon } from '@heroicons/react/solid'
import { useIntl } from 'lib/intl-utils'
import Link from 'components/base/Link'
import { match } from 'ts-pattern'
import PhoneNumberInput from 'components/base/PhoneNumberInput'

type Props = {
  processState: RecoveryProcessState
  setProcessState: Updater<RecoveryProcessState>
}

const ResetPasswordForm: FC<Props> = ({ processState, setProcessState }) => {
  const ctx = useUserContext()
  const { t, lang } = useIntl()
  const [generalError, setGeneralError] = useState<string | null>(null)
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<RecoveryFormData>({ mode: 'onSubmit' })

  const onUserDataSubmit = useCallback(async (data: RecoveryFormData) => {
    window.scrollTo({ top: 0 })

    const response = await ctx.recoverySendAuthCode(data.phone)

    match(response)
      .with({ status: 'AuthCodeSent' }, () => {
        setProcessState((s) => {
          s.stage = 'waitingForCode'
          s.data = data
          s.codeType = response.type
          s.phone = response.phone
        })
      })
      .with({ status: 'UserNotFound' }, () => {
        setGeneralError(t`login.user_not_found`)
      })
      .with({ status: 'AttemptsLimitReached' }, (response) => {
        setGeneralError(
          t`login.attempts_limit_reached` +
            ` ${response.seconds} ` +
            t('second_accusative', { count: response.seconds })
        )
      })
      .exhaustive()
  }, [])

  useEffect(() => {
    if (!processState.data) return
    reset(processState.data)
  }, [processState.data])

  return (
    <>
      <div className="mt-20">
        <Link
          href="/login/"
          className="font-medium text-gray-400 hover:underline"
          innerClassName="flex items-center space-x-1"
        >
          <ArrowNarrowLeftIcon className="size-3.5" />
          <span>{t`go_back`}</span>
        </Link>
        <h2 className="mt-4 text-xl font-medium text-gray-900">{t`login.reset_password`}</h2>
        <p className="w-full mt-2 text-sm text-gray-700">{t`login.specify_phone`}</p>
      </div>
      <form
        onSubmit={handleSubmit(onUserDataSubmit)}
        className="grid grid-cols-1 mt-10 gap-y-6 gap-x-6 sm:grid-cols-2"
      >
        <FormItem label={t`phone`} error={errors.phone?.message} className="col-span-full">
          <PhoneNumberInput
            name="phone"
            rules={{
              required: t`required_field`,
            }}
            control={control as any}
            hasError={Boolean(errors.phone?.message)}
          />
        </FormItem>
        <div className="space-y-2 col-span-full">
          {generalError && <p className="text-red-600">{generalError}</p>}
          <Button type="submit" theme="primary" className="justify-center w-full space-x-1">
            <span>{t`reset_password`}</span>
          </Button>
        </div>
      </form>
    </>
  )
}

export default ResetPasswordForm
